"use client";

import { useTablePage } from "@/lib/providers";
import { useStatCardFilters, useTransitioningValue, type StatFilterPreset } from "@/lib/hooks";
import { StatCardButton, type StatCardColor } from "./StatCard";
import { cn } from "@/lib/utils";

// ─── Internal base card config ──────────────────────────────────────────────

interface BaseCardConfig {
  key: string;
  label: string;
  color: StatCardColor;
  views: Array<"standard" | "archived">;
}

const BASE_CARDS: BaseCardConfig[] = [
  {
    key: "all_submissions",
    label: "All submissions",
    color: "blue",
    views: ["standard", "archived"],
  },
  {
    key: "processed_submissions",
    label: "New",
    color: "green",
    views: ["standard", "archived"],
  },
  {
    key: "review_submissions",
    label: "Review",
    color: "amber",
    views: ["standard", "archived"],
  },
];

const BASE_PRESETS: StatFilterPreset[] = [
  { key: "all_submissions", filter: null },
  {
    key: "processed_submissions",
    filter: { column: "status", operator: "EQ", value: "processed" },
  },
  {
    key: "review_submissions",
    filter: { column: "status", operator: "EQ", value: "review" },
  },
];

// ─── Custom card type ───────────────────────────────────────────────────────

export interface CustomStatCard extends StatFilterPreset {
  label: string;
  value: string;
  color: StatCardColor;
  views?: Array<"standard" | "archived">;
}

// ─── Component ──────────────────────────────────────────────────────────────

interface StatCardFiltersProps {
  baseCardsValues: Record<string, string>;
  customCardsCollection?: CustomStatCard[];
  presetOverrides?: Partial<Record<string, StatFilterPreset["filter"]>>;
  className?: string;
  /**
   * When `true`, clicking a card keeps any unrelated active filter chips and
   * only swaps the status dimension the cards own. Defaults to `false`
   * (clicking a card replaces the whole filter set).
   */
  preserveOtherFilters?: boolean;
}

export function StatCardFilters({
  baseCardsValues,
  customCardsCollection = [],
  presetOverrides,
  className,
  preserveOtherFilters = false,
}: StatCardFiltersProps) {
  const { searchParams, currentFilters, handleFiltersChange, isPending } = useTablePage();

  const allPresets: StatFilterPreset[] = [
    ...BASE_PRESETS.map(function applyOverride(preset) {
      if (presetOverrides && preset.key in presetOverrides) {
        return { ...preset, filter: presetOverrides[preset.key]! };
      }
      return preset;
    }),
    ...customCardsCollection.map(function toPreset(card) {
      return { key: card.key, filter: card.filter };
    }),
  ];

  const { activePresetKey, onStatCardClick } = useStatCardFilters({
    filters: currentFilters,
    presets: allPresets,
    onFiltersChange: handleFiltersChange,
    preserveOtherFilters,
  });

  const showArchived = searchParams.get("show_archived") === "true";
  const isViewChanging = useTransitioningValue(showArchived, isPending);
  const currentView = showArchived ? "archived" : "standard";

  const visibleBaseCards = BASE_CARDS.filter(function isVisibleInView(card) {
    return card.views.includes(currentView);
  }).map(function toRenderable(card) {
    return {
      key: card.key,
      label: card.label,
      color: card.color,
      value: baseCardsValues[card.key],
    };
  });

  const visibleCustomCards = customCardsCollection
    .filter(function isVisibleInView(card) {
      const cardViews = card.views ?? ["standard"];
      return cardViews.includes(currentView);
    })
    .map(function toRenderable(card) {
      return {
        key: card.key,
        label: card.label,
        color: card.color,
        value: card.value,
      };
    });

  const allCards = [...visibleBaseCards, ...visibleCustomCards];

  if (allCards.length === 0) return null;

  return (
    <div
      className={cn(
        "grid gap-3 mb-4 grid-cols-[repeat(auto-fit,minmax(160px,1fr))]",
        className,
      )}
    >
      {allCards.map(function renderCard(card) {
        if (!card.value && !isViewChanging) return null;

        return (
          <StatCardButton
            key={card.key}
            color={card.color}
            label={card.label}
            value={card.value ?? ""}
            loading={isViewChanging}
            active={!isViewChanging && activePresetKey === card.key}
            aria-label={`${card.label}: ${isViewChanging ? "loading" : card.value}`}
            onClick={function handleClick() {
              onStatCardClick(card.key);
            }}
          />
        );
      })}
    </div>
  );
}
